import React, { useEffect, useState } from 'react'
import { Box, Fab } from '@mui/material'
import { useSessionContext } from 'session'
import { type TableEntity, type UsFilters, UsTable, type TableColumn, type Chamber } from 'components'
import {
  type Committee,
  type CommitteeFilterResults,
  committeeListFilter,
  type Role,
  type State
} from 'services'
import { CommitteeFilters } from 'components'
import { US_COMMITTEE_ROLES } from 'utils'
import AddIcon from '@mui/icons-material/Add'
import type { Row } from 'react-table'

export interface CommiteeFilters extends UsFilters {
  type: Role | null
  state: State | null
  chamber: Chamber | null
  name: string | null
  subs: boolean
  active: boolean | null
}
export const CommitteePage: React.FC = () => {
  const { changeModuleName, changeUniqueId } = useSessionContext()
  const [committees, setCommittees] = useState<TableEntity[]>([])
  const initialState: CommiteeFilters = {
    type: null,
    state: null,
    name: null,
    chamber: null,
    subs: true,
    active: null,
    page: 1,
    order_field: 'id',
    order_direction: 'ASC'
  }

  const defaultAdditionalColumns = [
    { Header: 'Name', accessor: 'name', order_field: 'name' },
    { Header: 'Chamber', accessor: 'chamber.desc', order_field: 'chamber' },
    { Header: 'State', accessor: 'state.desc', order_field: 'state' },
    { Header: 'Type', accessor: 'role.desc', order_field: 'role' },
    {
      Header: 'Status',
      accessor: 'active',
      order_field: 'active',
      Cell: ({ row }: { row: Row<Committee> }) => {
        if (row.original.active === true) {
          return 'Active'
        } else {
          return 'Inactive'
        }
      }
    }
  ]

  const getAdditionalColumns = (): TableColumn[] => {
    const additionalColumns: TableColumn[] = [
      ...(filters.subs ? [{ Header: 'Parent', accessor: 'parent.name', order_field: 'parent' }] : [])
    ]

    return [...defaultAdditionalColumns, ...additionalColumns]
  }

  const [filters, setFilters] = useState<CommiteeFilters>(initialState)
  const [lastQueriedFilters, setLastQueriedFilters] = useState<CommiteeFilters>(initialState)
  const [loadResultWithFilters, setLoadResultWithFilters] = useState(false)
  const [pageCount, setPageCount] = useState<number>(0)
  const [totalCount, setTotalCount] = useState<number | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [additionalColumns, setAdditionalColumns] = useState<TableColumn[]>(getAdditionalColumns())
  const { openNewTab } = useSessionContext()

  useEffect(() => {
    changeModuleName('Committees')
    changeUniqueId('')
  }, [changeModuleName])

  const updateFilters = (filters: any): void => {
    setFilters(prevState => ({ ...prevState, ...filters }))
  }

  const cleanFilter = (): void => {
    const diff = JSON.stringify(initialState) === JSON.stringify(lastQueriedFilters)
    setFilters(initialState)
    if (!diff) { setLoadResultWithFilters(prevState => !prevState) }
  }

  const getTypeFilterValues = (): number[] | undefined => {
    if (filters.type == null) {
      return filters.subs
        ? Object.values(US_COMMITTEE_ROLES)
        : Object.values(US_COMMITTEE_ROLES).filter(role => role !== US_COMMITTEE_ROLES.FED_SUBCOMMITTEE && role !== US_COMMITTEE_ROLES.STATE_SUBCOMMITTEE)
    }
    if (filters.type.id === US_COMMITTEE_ROLES.FED_COMMITTEE && filters.subs) {
      return [US_COMMITTEE_ROLES.FED_COMMITTEE, US_COMMITTEE_ROLES.FED_SUBCOMMITTEE]
    }

    if (filters.type.id === US_COMMITTEE_ROLES.STATE_COMMITTEE && filters.subs) {
      return [US_COMMITTEE_ROLES.STATE_COMMITTEE, US_COMMITTEE_ROLES.STATE_SUBCOMMITTEE]
    }

    return [filters.type.id]
  }

  const fetchCommittees = async (): Promise<CommitteeFilterResults> => {
    setLoading(true)

    try {
      const params: any = {
        chamber: filters.chamber != null ? filters.chamber.id : undefined,
        name: filters.name != null ? filters.name : undefined,
        state: filters.state != null ? filters.state.id : undefined,
        type: getTypeFilterValues(),
        active: filters.active,
        page: filters.page !== 0 ? filters.page : undefined
      }
      params.order_by = filters.order_field
      params.order_direction = filters.order_direction
      params.page_size = 200
      return await committeeListFilter(params)
    } catch (error) {
      console.error(error)
      return { data: [], page_number: 1, page_size: 1, total_pages: 1, total_items: 0 }
    }
  }

  const updateCommitteeTableWithFilter = async (): Promise<void> => {
    fetchCommittees().then(response => {
      setCommittees(response.data)
      setPageCount(response.total_pages)
      setTotalCount(response.total_items)
      setLastQueriedFilters(filters)
      if (filters.page !== response.page_number) { updateFilters({ page: response.page_number }) }
      setAdditionalColumns(getAdditionalColumns())
    }).catch(reason => {
      console.log(reason)
      setCommittees([])
      setPageCount(0)
    }).finally(() => { setLoading(false) })
  }

  useEffect(() => {
    const updateTablePage = async (): Promise<void> => {
      await updateCommitteeTableWithFilter()
    }
    void updateTablePage()
  }, [loadResultWithFilters])

  return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <Box width='97%' sx={{ pb: 1 }}>
                    <CommitteeFilters doFilter={updateCommitteeTableWithFilter} filters={filters} updateFilters={updateFilters} cleanFilters={cleanFilter}/>
                    <Box sx={{ display: 'flex', justifyContent: 'end', mb: 2 }}>
                      <Fab color="primary" aria-label="add" onClick={() => { openNewTab('/us/committee/create') }}>
                        <AddIcon/>
                      </Fab>
                    </Box>
                  <UsTable entities={committees} orderField={filters.order_field} orderDirection={filters.order_direction} loading={loading}
                          pageCount={pageCount} totalCount={totalCount} triggerReload={() => { setLoadResultWithFilters(prevState => !prevState) }} openEdit={(entity) => { entity.id != null ? openNewTab(`/us/committee/edit/${entity.id}`) : alert('No ID on this Committee 🤔') }}
                          filters={filters}
                           updateFilters={updateFilters}
                           openDelete={() => { alert('Committee Deleted! :p jk we can\'t do that yet') }}
                           additionalColumns={additionalColumns}
                    />
                </Box>
            </Box>
  )
}
